import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { money, currentWeek } from "@/lib/format";
import { StatusPill } from "@/components/StatusPill";
import ExpenseDialog from "@/components/ExpenseDialog";
import { WeekSelect } from "@/components/WeekSelect";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2, Plus, Paperclip } from "lucide-react";

const STATUS_OPTS = [
  { v: "all", l: "All statuses" },
  { v: "submitted,query_raised", l: "Awaiting approval" },
  { v: "submitted", l: "Submitted" },
  { v: "query_raised", l: "Query raised" },
  { v: "approved", l: "Approved" },
  { v: "rejected", l: "Rejected" },
  { v: "draft", l: "Draft" },
];

export default function Expenses() {
  const { settings, isManager } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const cur = settings.currency || "£";
  const [rows, setRows] = useState(null);
  const [sites, setSites] = useState([]);
  const [cats, setCats] = useState([]);
  const [week, setWeek] = useState(params.get("week") || currentWeek());
  const [site, setSite] = useState(params.get("site_id") || "all");
  const [status, setStatus] = useState(params.get("status") || "all");
  const [cat, setCat] = useState("all");
  const [missing, setMissing] = useState(params.get("missing") === "true");
  const [selected, setSelected] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    api.get("/sites").then((r) => setSites(r.data));
    api.get("/categories").then((r) => setCats(r.data));
    const openId = params.get("open");
    if (openId) api.get(`/expenses/${openId}`).then((r) => { setSelected(r.data); setDialogOpen(true); }).catch(() => {});
  }, []);

  const load = useCallback(() => {
    const p = new URLSearchParams();
    if (week) p.append("week", week);
    if (site !== "all") p.append("site_id", site);
    if (status !== "all") p.append("status", status);
    if (cat !== "all") p.append("category", cat);
    if (missing) p.append("missing_receipt", "true");
    setRows(null);
    api.get(`/expenses?${p.toString()}`).then((r) => setRows(r.data));
  }, [week, site, status, cat, missing]);

  useEffect(() => { load(); }, [load]);

  const openRow = (e) => { setSelected(e); setDialogOpen(true); };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="font-head text-2xl lg:text-3xl font-semibold text-slate-900">Expenses</h1>
          <p className="text-slate-500 text-sm mt-1">{isManager ? "Review, approve, query or reject submitted expenses." : "Your submitted expenses."}</p>
        </div>
        <Button data-testid="add-expense-btn" onClick={() => nav("/add")} className="bg-[#0F172A] hover:bg-[#1E293B]"><Plus className="h-4 w-4 mr-1.5" /> Add Expense</Button>
      </div>

      <Card className="border-border p-4">
        <div className="flex flex-wrap items-center gap-3">
          <WeekSelect value={week} onChange={setWeek} />
          <Select value={site} onValueChange={setSite}>
            <SelectTrigger data-testid="filter-site" className="w-[160px] bg-white"><SelectValue placeholder="Home" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All homes</SelectItem>{sites.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger data-testid="filter-status" className="w-[180px] bg-white"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>{STATUS_OPTS.map((s) => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={cat} onValueChange={setCat}>
            <SelectTrigger data-testid="filter-category" className="w-[170px] bg-white"><SelectValue placeholder="Category" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All categories</SelectItem>{cats.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
          </Select>
          <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
            <Checkbox data-testid="filter-missing" checked={missing} onCheckedChange={(v) => setMissing(!!v)} /> Missing receipts only
          </label>
        </div>
      </Card>

      <Card className="border-border overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Merchant</TableHead>
                <TableHead>Home</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Staff</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead className="text-center">Receipt</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows === null && <TableRow><TableCell colSpan={7} className="text-center py-10"><Loader2 className="h-5 w-5 animate-spin mx-auto text-slate-400" /></TableCell></TableRow>}
              {rows && rows.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-slate-400 py-10">No expenses match these filters.</TableCell></TableRow>}
              {rows && rows.map((e) => (
                <TableRow key={e.id} data-testid={`expense-row-${e.ref}`} className="cursor-pointer hover:bg-slate-50" onClick={() => openRow(e)}>
                  <TableCell className="font-medium text-slate-800">{e.merchant}<div className="text-xs text-slate-400">{e.purchase_date}</div></TableCell>
                  <TableCell className="text-slate-600">{e.site_name}</TableCell>
                  <TableCell className="text-slate-600">{e.category}</TableCell>
                  <TableCell className="text-slate-600">{e.staff_name}</TableCell>
                  <TableCell className="text-right font-mono tabular-nums font-medium">{money(e.amount, cur)}</TableCell>
                  <TableCell className="text-center">{e.has_receipt ? <Paperclip className="h-4 w-4 text-emerald-600 mx-auto" /> : <span className="text-xs text-red-500">Missing</span>}</TableCell>
                  <TableCell><StatusPill status={e.status} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>

      <ExpenseDialog expense={selected} open={dialogOpen} onClose={() => setDialogOpen(false)} onChanged={load} />
    </div>
  );
}
